import { Button, Pagination, SearchBar, Checkbox } from '@ab-inbev-maz/compass';
import { useState } from 'react';
import { useErrorGroupsStore } from '../../stores/errorGroupsStore';
import type { ErrorGroup } from '../../services/errorGroupsService';
import StatusBadge from '../commos/StatusBadge';
import { useDebounce } from '../../hooks/useDebounce';
import { useErrorGroupsFetch } from '../../hooks/useErrorGroupsFetch';

// Reprocess button for individual group
const ReprocessGroupButton = ({ groupId, disabled }: { groupId: number; disabled: boolean }) => {
  const reprocessErrorGroups = useErrorGroupsStore((state) => state.reprocessErrorGroups);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleClick = async () => {
    try {
      setIsProcessing(true);
      await reprocessErrorGroups({ error_group_ids: [groupId] });
    } catch (error) {
      console.error('Error al reprocesar grupo:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Button
      className='border-none cursor-pointer'
      icon={'arrow/repeat'}
      size="sm"
      variant="secondary"
      onClick={handleClick}
      disabled={disabled || isProcessing}
    />
  );
};

const SimpleErrorGroupsTable = () => {
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Use store directly instead of local state
  const errorGroups = useErrorGroupsStore((state) => state.errorGroups);
  const totalCount = useErrorGroupsStore((state) => state.totalCount);
  const loading = useErrorGroupsStore((state) => state.loading);
  const currentPage = useErrorGroupsStore((state) => state.currentPage);
  const pageSize = useErrorGroupsStore((state) => state.pageSize);
  const setCurrentPage = useErrorGroupsStore((state) => state.setCurrentPage);
  const reprocessErrorGroups = useErrorGroupsStore((state) => state.reprocessErrorGroups);

  // Use custom hooks
  const debouncedSearch = useDebounce(searchValue, 500);
  useErrorGroupsFetch({ searchValue: debouncedSearch, currentPage, pageSize });

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const isRowDisabled = (row: ErrorGroup) => {
    return row.reprocessable === false || row.status === 'processing' || row.status === 'resolved';
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const enabledIds = errorGroups
        .filter(group => !isRowDisabled(group))
        .map(group => group.id);
      setSelectedIds(new Set(enabledIds));
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (groupId: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(groupId);
    } else {
      newSelected.delete(groupId);
    }
    setSelectedIds(newSelected);
  };

  const handleReprocess = async () => {
    if (selectedIds.size === 0) return;
    const errorGroupIds = Array.from(selectedIds);
    await reprocessErrorGroups({ error_group_ids: errorGroupIds });
    setSelectedIds(new Set()); // Clear selection after reprocessing
  };

  const enabledGroups = errorGroups.filter(group => !isRowDisabled(group));
  const allSelectedEnabled = enabledGroups.length > 0 && enabledGroups.every(group => selectedIds.has(group.id));

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-sm w-full space-y-8 bg-white p-8 rounded-3xl shadow-lg">
          <div className="text-center">Cargando grupos de errores...</div>
        </div>
      </div>
    );
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold mb-2">Grupos de Errores</h2>
          <p className="text-gray-600">Total: {totalCount} grupos</p>
        </div>

        {/* Search Bar and Actions */}
        <div className="mb-4 flex items-center gap-4">
          <SearchBar
            id="search-bar-groups"
            name="Buscar"
            variant="default"
            leftIcon="search"
            className="w-full max-w-md rounded-md"
            placeholder="Buscar..."
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchValue(e.target.value)}
            onClear={() => setSearchValue("")}
          />
          <Button
            label="Reprocesar"
            variant="secondary"
            rightIcon="arrow/repeat"
            onClick={handleReprocess}
            disabled={selectedIds.size === 0}
            className="cursor-pointer"
          />
        </div>

        {/* Simple Table */}
        <div className="overflow-x-auto bg-white rounded-lg shadow">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 w-12">
                  <Checkbox
                    checked={allSelectedEnabled}
                    onChange={handleSelectAll}
                    disabled={enabledGroups.length === 0}
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tipo de Error
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  SKU
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Combos Afectados
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mensaje
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Última Ocurrencia
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Intentos
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {errorGroups.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-12 text-center text-gray-500">
                    No hay grupos de errores disponibles
                  </td>
                </tr>
              ) : (
                errorGroups.map((group) => {
                  const disabled = isRowDisabled(group);
                  const checked = selectedIds.has(group.id);
                  return (
                    <tr key={group.id} className={`hover:bg-gray-50 ${disabled ? 'opacity-50' : ''}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <Checkbox
                          checked={checked}
                          onChange={(e) => handleSelectRow(group.id, e.target.checked)}
                          disabled={disabled}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {group.error_type}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {group.sku || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <StatusBadge status={group.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {group.affected_combos_count}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-md truncate">
                        {group.reason}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(group.last_occurrence).toLocaleDateString()}{' '}
                        {new Date(group.last_occurrence).toLocaleTimeString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {group.reprocess_attempts}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex gap-2 items-center">
                          {/*<Button
                            className='border-none cursor-pointer'
                            label="ℹ️"
                            size="sm"
                            variant="secondary"
                            onClick={() => handleViewDetails(group.id)}
                            title="Ver detalles"
                          />*/}
                          <ReprocessGroupButton groupId={group.id} disabled={disabled} />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex justify-center">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </div>
  );
};

export default SimpleErrorGroupsTable;
